/* Live Shopify search. Native form submission remains the no-JavaScript fallback. */
(() => {
  'use strict';
  if (!window.customElements || customElements.get('oh-live-search')) return;

  class OddHabitLiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.input = this.querySelector('[name="q"]');
      this.form = this.querySelector('form');
      this.list = this.querySelector('[data-live-results]');
      this.status = this.querySelector('[data-live-status]');
      this.clearButton = this.querySelector('[data-live-clear]');
      this.allLink = this.querySelector('[data-live-all]');
      this.dialog = this.closest('dialog');
      if (!this.input || !this.form || !this.list || !this.status || !this.clearButton || !this.allLink) return;
      this.bound = true;
      this.revision = 0;
      this.active = -1;
      this.cache = new Map();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-controls', this.list.id);
      this.input.setAttribute('aria-expanded', 'false');
      this.input.addEventListener('input', event => { if (!event.isComposing && !this.composing) this.queue(); }, options);
      this.input.addEventListener('compositionstart', () => { this.composing = true; this.cancel(); }, options);
      this.input.addEventListener('compositionend', () => { this.composing = false; this.queue(); }, options);
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim() && this.list.hidden && !this.pending && !this.composing) this.queue();
      }, options);
      this.input.addEventListener('keydown', event => this.onKey(event), options);
      this.clearButton.addEventListener('click', () => { this.input.value = ''; this.queue(); this.input.focus(); }, options);
      this.form.addEventListener('submit', event => {
        if (this.active >= 0 && !this.list.hidden) {
          const link = this.list.children[this.active]?.querySelector('a');
          if (link) { event.preventDefault(); link.click(); }
        }
      }, options);
      this.dialog?.addEventListener('close', () => { this.cancel(); this.hide(); }, options);
      this.clearButton.hidden = !this.input.value;
    }

    disconnectedCallback() {
      this.cancel();
      this.events?.abort();
      this.bound = false;
    }

    cancel() {
      this.revision = (this.revision || 0) + 1;
      clearTimeout(this.debounce);
      clearTimeout(this.deadline);
      this.request?.abort();
      this.request = null;
      this.pending = false;
    }

    hide() {
      this.list.hidden = true;
      this.list.replaceChildren();
      this.list.setAttribute('aria-busy', 'false');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
      this.allLink.hidden = true;
      this.status.textContent = '';
    }

    queue() {
      this.cancel();
      this.hide();
      const term = this.input.value.trim();
      this.clearButton.hidden = !this.input.value;
      if (!term) return;
      const url = new URL(this.form.action, location.href);
      url.search = new URLSearchParams({q: term, type: 'product', 'options[prefix]': 'last'}).toString();
      this.allLink.href = this.previewURL(url);
      this.allLink.textContent = 'View all results';
      this.status.textContent = 'Searching…';
      this.list.setAttribute('aria-busy', 'true');
      this.pending = true;
      const revision = this.revision;
      this.debounce = setTimeout(() => this.search(term, revision), 200);
    }

    previewURL(url) {
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url.pathname + url.search + url.hash;
    }

    async search(term, revision) {
      const key = term.toLocaleLowerCase();
      const cached = this.cache.get(key);
      if (cached && Date.now() - cached.time < 60000) {
        if (revision === this.revision) this.render(cached.results);
        return;
      }
      const controller = new AbortController();
      this.request = controller;
      const deadline = setTimeout(() => controller.abort(), 8000);
      this.deadline = deadline;
      try {
        const url = new URL(this.dataset.endpoint, location.href);
        if (!url.pathname.endsWith('.json')) url.pathname = url.pathname.replace(/\/$/, '') + '.json';
        url.search = new URLSearchParams({
          q: term,
          'resources[type]': 'product,collection',
          'resources[limit]': '6',
          'resources[limit_scope]': 'each',
          'resources[options][unavailable_products]': 'show'
        }).toString();
        const response = await fetch(url, {signal: controller.signal, credentials: 'same-origin', headers: {Accept: 'application/json'}});
        if (!response.ok) throw new Error('Search request failed');
        const data = await response.json();
        const results = data?.resources?.results;
        if (!results || (!Array.isArray(results.products) && !Array.isArray(results.collections))) throw new Error('Invalid search response');
        if (revision !== this.revision || !this.isConnected) return;
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(key, {time: Date.now(), results});
        this.render(results);
      } catch (error) {
        if (revision !== this.revision || !this.isConnected) return;
        this.hide();
        this.status.textContent = 'Live search is unavailable. Use View all results.';
        this.allLink.hidden = false;
      } finally {
        clearTimeout(deadline);
        if (revision === this.revision) {
          this.pending = false;
          this.list.setAttribute('aria-busy', 'false');
          this.request = null;
        }
      }
    }

    render(results) {
      this.hide();
      this.pending = false;
      const rows = [
        ...(results.products || []).slice(0, 6).map(item => ({item, kind: 'Product'})),
        ...(results.collections || []).slice(0, 3).map(item => ({item, kind: 'Collection'}))
      ];
      rows.forEach(({item, kind}) => {
        if (!item?.url || !item?.title) return;
        let url;
        try { url = new URL(item.url, location.href); } catch (_) { return; }
        if (url.origin !== location.origin || !/^https?:$/.test(url.protocol)) return;
        const row = document.createElement('li');
        row.id = this.list.id + '-' + this.list.children.length;
        row.setAttribute('role', 'option');
        row.setAttribute('aria-selected', 'false');
        row.setAttribute('aria-label', kind + ': ' + item.title);
        const link = document.createElement('a');
        link.className = 'oh-search-result';
        link.href = this.previewURL(url);
        link.tabIndex = -1;
        const thumb = document.createElement('span');
        thumb.className = 'oh-search-thumb';
        const source = item.featured_image?.url || item.image?.url || item.image?.src || (typeof item.image === 'string' ? item.image : null);
        if (source) {
          try {
            const imageURL = new URL(source, location.href);
            if (/^https?:$/.test(imageURL.protocol)) {
              if (imageURL.hostname.endsWith('.shopify.com')) imageURL.searchParams.set('width', '180');
              const img = new Image();
              img.src = imageURL.href; img.alt = ''; img.width = 64; img.height = 76; img.loading = 'lazy';
              thumb.append(img);
            }
          } catch (_) { /* A missing thumbnail never blocks navigation. */ }
        }
        const text = document.createElement('span');
        text.className = 'oh-search-text';
        const title = document.createElement('span'); title.textContent = item.title;
        const label = document.createElement('small'); label.textContent = kind;
        text.append(title, label); link.append(thumb, text); row.append(link); this.list.append(row);
      });
      const count = this.list.children.length;
      this.list.hidden = count === 0;
      this.input.setAttribute('aria-expanded', String(count > 0));
      this.status.textContent = count ? `${count} ${count === 1 ? 'result' : 'results'}` : 'No results.';
      this.allLink.hidden = false;
    }

    onKey(event) {
      if (event.isComposing || this.composing) return;
      if (event.key === 'Escape' && (!this.list.hidden || this.pending || !this.allLink.hidden)) {
        event.preventDefault(); event.stopPropagation(); this.cancel(); this.hide(); return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.list.hidden) return;
      const rows = [...this.list.children];
      if (!rows.length) return;
      event.preventDefault();
      this.active = this.active < 0 ? (event.key === 'ArrowDown' ? 0 : rows.length - 1) : (this.active + (event.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length;
      rows.forEach((row, index) => row.setAttribute('aria-selected', String(index === this.active)));
      this.input.setAttribute('aria-activedescendant', rows[this.active].id);
      rows[this.active].scrollIntoView({block: 'nearest', behavior: 'instant'});
    }
  }
  customElements.define('oh-live-search', OddHabitLiveSearch);
})();
